import { UserDropdown, useAuthStatus } from './molecules/user_dropdown'
import { FlightList } from './molecules/flight_list'
import { FlightDetails } from './molecules/flight_details'
import { TakeoffDetails } from './molecules/takeoff_details'
import { TakeoffList } from './molecules/takeoff_list'
import { AdventureList } from './molecules/adventure_list'
import { AdventureDetails } from './molecules/adventure_details'
import { PilotList } from './molecules/pilot_list'
import { PilotProfile, MyProfile } from './molecules/pilot_profile'
import { UserSettings } from './molecules/user_settings'
import { ActivityFeed } from './molecules/activity_feed'
import { About } from './molecules/about'
import { AdminPage } from './molecules/admin'
import { AddFlightModal, ManualFlightData } from './molecules/add_flight_modal'
import { FlightPayload, PilotPayload, TakeOffPayload } from './molecules/types'
import { Navbar, NavbarDivider, NavbarItem, NavbarLabel, NavbarSection, NavbarSpacer } from './components/navbar'
import { Sidebar, SidebarBody, SidebarHeader, SidebarItem, SidebarLabel, SidebarSection } from './components/sidebar'
import { StackedLayout } from './components/stacked-layout'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import { PlusIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { Combobox, ComboboxOption, ComboboxLabel } from './components/combobox'
import DualRangeSlider from './molecules/dual_range_slider'
import SingleRangeSlider from './molecules/single_range_slider'

function AirtimeBrand({ compact = false }: { compact?: boolean }) {
  return (
    <span className="inline-flex items-center overflow-visible pl-1">
      <img
        src="/airtime.svg"
        alt="Airtime"
        aria-hidden="true"
        className={
          compact
            ? 'h-7 w-14 max-w-none origin-left scale-x-125 object-contain'
            : 'h-8 w-[4.5rem] max-w-none origin-left scale-x-125 object-contain'
        }
      />
    </span>
  )
}

function MainNavbar({ onAddFlight }: { onAddFlight?: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { userData } = useAuthStatus()

  const navItems = userData.authenticated
    ? [
        { label: 'Feed', url: '/' },
        { label: 'Flights', url: '/flights' },
        { label: 'Adventures', url: '/adventures' },
        { label: 'Pilots', url: '/pilots' },
        { label: 'Takeoffs', url: '/takeoffs' },
        { label: 'About', url: '/about' },
      ]
    : [
        { label: 'Flights', url: '/' },
        { label: 'Adventures', url: '/adventures' },
        { label: 'Pilots', url: '/pilots' },
        { label: 'Takeoffs', url: '/takeoffs' },
        { label: 'About', url: '/about' },
      ]

  if (userData.pilot?.is_admin) {
    navItems.push({ label: 'Admin', url: '/admin' })
  }

  return (
    <Navbar>
      <NavbarSection className="max-lg:hidden">
        <NavbarItem className="max-lg:hidden" onClick={() => navigate('/')}>
          <NavbarLabel className="overflow-visible">
            <AirtimeBrand compact />
          </NavbarLabel>
        </NavbarItem>
      </NavbarSection>
      <NavbarDivider className="max-lg:hidden" />
      <NavbarSection className="max-lg:hidden">
        {navItems.map(({ label, url }) => (
          <NavbarItem
            key={label}
            current={location.pathname === url}
            onClick={() => navigate(url)}
          >
            {label}
          </NavbarItem>
        ))}
      </NavbarSection>
      <NavbarSpacer />
      <NavbarSection>
        {userData.authenticated && onAddFlight && (
          <NavbarItem onClick={onAddFlight} aria-label="Add flight">
            <PlusIcon className="h-5 w-5" />
          </NavbarItem>
        )}
        <UserDropdown />
      </NavbarSection>
    </Navbar>
  )
}



const PAGE_SIZE = 50
const DURATION_MIN = 0
const DURATION_MAX = 600
const DURATION_STEP = 10
const DISTANCE_MIN = 0
const DISTANCE_MAX = 200
const DISTANCE_STEP = 5

interface FilterState {
  pilots: { nick: string; name: string }[]
  takeoffs: { id: number; name: string }[]
  after: string
  before: string
  flightType: string
  hasPhotos: string    // '' | 'true' | 'false'
  hasTrack: string     // '' | 'true' | 'false'
  hasUpTrack: string   // '' | 'true' | 'false'
  minDistance: string
  minDuration: string
  maxDuration: string
}

const emptyFilters: FilterState = {
  pilots: [], takeoffs: [], after: '', before: '',
  flightType: '', hasPhotos: '', hasTrack: '', hasUpTrack: '',
  minDistance: '', minDuration: '', maxDuration: '',
}

function buildFlightParams(offset: number, filters: FilterState): URLSearchParams {
  const params = new URLSearchParams()
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  if (filters.pilots.length > 0) params.set('pilots', filters.pilots.map(p => p.nick).join(','))
  if (filters.takeoffs.length > 0) params.set('takeoffs', filters.takeoffs.map(t => String(t.id)).join(','))
  if (filters.after) params.set('after', filters.after)
  if (filters.before) params.set('before', filters.before)
  if (filters.flightType) params.set('flight_type', filters.flightType)
  if (filters.hasPhotos) params.set('has_photos', filters.hasPhotos)
  if (filters.hasTrack) params.set('has_track', filters.hasTrack)
  if (filters.hasUpTrack) params.set('has_up_track', filters.hasUpTrack)
  if (filters.minDistance) params.set('min_distance', filters.minDistance)
  if (filters.minDuration) params.set('min_duration', filters.minDuration)
  if (filters.maxDuration) params.set('max_duration', filters.maxDuration)
  return params
}

function hasActiveFilters(f: FilterState) {
  return f.pilots.length > 0 || f.takeoffs.length > 0 || f.after !== '' || f.before !== ''
    || f.flightType !== '' || f.hasPhotos !== '' || f.hasTrack !== '' || f.hasUpTrack !== ''
    || f.minDistance !== '' || f.minDuration !== '' || f.maxDuration !== ''
}

function flightTypeLabel(ft: string): string {
  switch (ft) {
    case 'site flight': return 'Site flight'
    case 'hike and fly': return 'Hike and fly'
    case 'tandem': return 'Tandem'
    default: return ft
  }
}

/** Format a Date as YYYY-MM-DD for date inputs and the API. */
function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10)
}

type DatePreset = { label: string; after: string; before: string }

function getDatePresets(): DatePreset[] {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)) // Monday

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)

  return [
    { label: 'Today', after: toDateString(today), before: toDateString(today) },
    { label: 'Yesterday', after: toDateString(yesterday), before: toDateString(yesterday) },
    { label: 'This week', after: toDateString(weekStart), before: toDateString(today) },
    { label: 'This month', after: toDateString(monthStart), before: toDateString(today) },
  ]
}

function FlightsList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [flights, setFlights] = useState<FlightPayload[]>([])
  const [, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // Seed filter state from URL search params (e.g. ?pilot=nick&pilot_name=Name)
  const initialFilters = useMemo<FilterState>(() => {
    const pilotNick = searchParams.get('pilot')
    const pilotName = searchParams.get('pilot_name')
    if (pilotNick) {
      return { ...emptyFilters, pilots: [{ nick: pilotNick, name: pilotName || pilotNick }] }
    }
    return emptyFilters
  }, []) // eslint-disable-line react-hooks/exhaustive-deps — only on mount

  const [filterOpen, setFilterOpen] = useState(() => hasActiveFilters(initialFilters))
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [allPilots, setAllPilots] = useState<PilotPayload[]>([])
  const [selectedPilot, setSelectedPilot] = useState<PilotPayload | null>(null)
  const [allTakeoffs, setAllTakeoffs] = useState<TakeOffPayload[]>([])
  const [selectedTakeoff, setSelectedTakeoff] = useState<TakeOffPayload | null>(null)

  // Clear URL search params after seeding so they don't linger on subsequent filter changes
  useEffect(() => {
    if (searchParams.has('pilot')) {
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps — only on mount

  useEffect(() => {
    if (filterOpen && allPilots.length === 0) {
      fetch('/v0/pilots')
        .then(r => r.json())
        .then(data => setAllPilots(data.pilots ?? []))
    }
    if (filterOpen && allTakeoffs.length === 0) {
      fetch('/v0/takeoffs')
        .then(r => r.json())
        .then(data => setAllTakeoffs(data.takeoffs ?? []))
    }
  }, [filterOpen, allPilots.length, allTakeoffs.length])

  const runFetch = useCallback(async (offset: number, append: boolean) => {
    try {
      const r = await fetch(`/v0/flights?${buildFlightParams(offset, filters)}`)
      if (!r.ok) throw new Error('Failed to fetch flights')
      const data = await r.json()
      if (append) {
        setFlights(prev => [...prev, ...(data.flights ?? [])])
      } else {
        setFlights(data.flights ?? [])
      }
      setHasMore(data.has_more ?? false)
    } catch (err) {
      console.error('Error fetching flights:', err)
    } finally {
      setIsLoading(false)
      setLoadingMore(false)
    }
  }, [filters])

  useEffect(() => {
    setIsLoading(true)
    runFetch(0, false)
  }, [runFetch])

  const handleLoadMore = () => {
    setLoadingMore(true)
    runFetch(flights.length, true)
  }

  const handlePilotSelect = (p: PilotPayload | null) => {
    if (p && !filters.pilots.find(x => x.nick === p.nick)) {
      setFilters(prev => ({ ...prev, pilots: [...prev.pilots, { nick: p.nick, name: p.name }] }))
    }
    setSelectedPilot(null)
  }

  const removePilot = (nick: string) => {
    setFilters(prev => ({ ...prev, pilots: prev.pilots.filter(p => p.nick !== nick) }))
  }

  const handleTakeoffSelect = (t: TakeOffPayload | null) => {
    if (t && !filters.takeoffs.find(x => x.id === t.id)) {
      setFilters(prev => ({ ...prev, takeoffs: [...prev.takeoffs, { id: t.id, name: t.name }] }))
    }
    setSelectedTakeoff(null)
  }

  const removeTakeoff = (id: number) => {
    setFilters(prev => ({ ...prev, takeoffs: prev.takeoffs.filter(t => t.id !== id) }))
  }

  const handleClear = () => {
    setFilters(emptyFilters)
    setSelectedPilot(null)
    setSelectedTakeoff(null)
  }

  const addPilotFilter = (nick: string) => {
    setFilters(prev =>
      prev.pilots.find(p => p.nick === nick)
        ? prev
        : { ...prev, pilots: [...prev.pilots, { nick, name: nick }] }
    )
  }

  const removePilotFilter = (nick: string) => {
    setFilters(prev => ({ ...prev, pilots: prev.pilots.filter(p => p.nick !== nick) }))
  }

  const addTakeoffFilter = (id: number, name: string) => {
    setFilters(prev =>
      prev.takeoffs.find(t => t.id === id)
        ? prev
        : { ...prev, takeoffs: [...prev.takeoffs, { id, name }] }
    )
  }

  const removeTakeoffFilter = (id: number) => {
    setFilters(prev => ({ ...prev, takeoffs: prev.takeoffs.filter(t => t.id !== id) }))
  }

  const removeDateFilter = (field: 'after' | 'before') => {
    setFilters(prev => ({ ...prev, [field]: '' }))
  }

  const isFiltered = hasActiveFilters(filters)
  const datePresets = getDatePresets()

  const applyDatePreset = (preset: DatePreset) => {
    setFilters(prev => ({ ...prev, after: preset.after, before: preset.before }))
  }

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-[#2D3142] dark:text-white">Flights</h1>
            <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
              All flights across the community.
            </p>
          </div>
          <div className="flex items-center gap-2 mt-4 sm:mt-0 sm:ml-4">
            <button
              onClick={() => setFilterOpen(v => !v)}
              title="Filter flights"
              className={`p-2 rounded-lg transition-colors ${
                isFiltered || filterOpen
                  ? 'bg-[#EF8354]/10 text-[#EF8354]'
                  : 'text-[#4F5D75] hover:bg-[#BFC0C0]/20 dark:text-[#BFC0C0] dark:hover:bg-[#4F5D75]/20'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {filterOpen && (
          <div className="mt-4 p-4 rounded-xl bg-white/80 dark:bg-[#2D3142]/60 border border-[#BFC0C0]/40 dark:border-[#4F5D75]/30 shadow-sm space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">Pilots</label>
              {filters.pilots.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {filters.pilots.map(p => (
                    <span
                      key={p.nick}
                      className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]"
                    >
                      {p.name || p.nick}
                      <button onClick={() => removePilot(p.nick)} className="hover:text-[#EF8354]/60">
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Combobox
                options={allPilots.filter(p => !filters.pilots.find(x => x.nick === p.nick))}
                value={selectedPilot ?? undefined}
                onChange={handlePilotSelect}
                displayValue={p => p?.name}
                placeholder="Search pilots…"
              >
                {p => (
                  <ComboboxOption value={p}>
                    <ComboboxLabel>{p.name} ({p.nick})</ComboboxLabel>
                  </ComboboxOption>
                )}
              </Combobox>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">From</label>
                <input
                  type="date"
                  value={filters.after}
                  onChange={e => setFilters(prev => ({ ...prev, after: e.target.value }))}
                  className="w-full rounded-lg border border-zinc-950/10 dark:border-white/10 bg-transparent dark:bg-white/5 px-3 py-1.5 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#EF8354]/50 dark:scheme-dark"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">To</label>
                <input
                  type="date"
                  value={filters.before}
                  onChange={e => setFilters(prev => ({ ...prev, before: e.target.value }))}
                  className="w-full rounded-lg border border-zinc-950/10 dark:border-white/10 bg-transparent dark:bg-white/5 px-3 py-1.5 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#EF8354]/50 dark:scheme-dark"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {datePresets.map(preset => {
                const isActive = filters.after === preset.after && filters.before === preset.before
                return (
                  <button
                    key={preset.label}
                    onClick={() => applyDatePreset(preset)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                      isActive
                        ? 'bg-[#EF8354] text-white'
                        : 'bg-[#BFC0C0]/20 text-[#4F5D75] hover:bg-[#EF8354]/10 hover:text-[#EF8354] dark:bg-[#4F5D75]/20 dark:text-[#BFC0C0] dark:hover:bg-[#EF8354]/15 dark:hover:text-[#EF8354]'
                    }`}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">Takeoffs</label>
              {filters.takeoffs.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {filters.takeoffs.map(t => (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]"
                    >
                      {t.name}
                      <button onClick={() => removeTakeoff(t.id)} className="hover:text-[#EF8354]/60">
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Combobox
                options={allTakeoffs.filter(t => !filters.takeoffs.find(x => x.id === t.id))}
                value={selectedTakeoff ?? undefined}
                onChange={handleTakeoffSelect}
                displayValue={t => t?.name}
                placeholder="Search takeoffs…"
              >
                {t => (
                  <ComboboxOption value={t}>
                    <ComboboxLabel>{t.name}</ComboboxLabel>
                  </ComboboxOption>
                )}
              </Combobox>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">Flight type</label>
              <div className="flex flex-wrap gap-2">
                {(['site flight', 'hike and fly', 'tandem'] as const).map(ft => (
                  <button
                    key={ft}
                    onClick={() => setFilters(prev => ({ ...prev, flightType: prev.flightType === ft ? '' : ft }))}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                      filters.flightType === ft
                        ? 'bg-[#EF8354] text-white'
                        : 'bg-[#BFC0C0]/20 text-[#4F5D75] hover:bg-[#EF8354]/10 hover:text-[#EF8354] dark:bg-[#4F5D75]/20 dark:text-[#BFC0C0] dark:hover:bg-[#EF8354]/15 dark:hover:text-[#EF8354]'
                    }`}
                  >
                    {flightTypeLabel(ft)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-1">Content</label>
              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'hasPhotos' as const, label: 'Has photos' },
                  { key: 'hasTrack' as const, label: 'Has track' },
                  { key: 'hasUpTrack' as const, label: 'Has up track' },
                ]).map(({ key, label }) => {
                  const val = filters[key]
                  return (
                    <button
                      key={key}
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        [key]: prev[key] === '' ? 'true' : prev[key] === 'true' ? 'false' : '',
                      }))}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        val === 'true'
                          ? 'bg-[#EF8354] text-white'
                          : val === 'false'
                            ? 'bg-zinc-500 text-white'
                            : 'bg-[#BFC0C0]/20 text-[#4F5D75] hover:bg-[#EF8354]/10 hover:text-[#EF8354] dark:bg-[#4F5D75]/20 dark:text-[#BFC0C0] dark:hover:bg-[#EF8354]/15 dark:hover:text-[#EF8354]'
                      }`}
                      title={val === '' ? `Click to require ${label.toLowerCase()}` : val === 'true' ? `Click to exclude ${label.toLowerCase()}` : `Click to clear`}
                    >
                      {val === 'false' ? `No ${label.toLowerCase().replace('has ', '')}` : label}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <SingleRangeSlider
                label="Min distance"
                min={DISTANCE_MIN}
                max={DISTANCE_MAX}
                step={DISTANCE_STEP}
                value={filters.minDistance ? Number(filters.minDistance) : DISTANCE_MIN}
                onChange={v => setFilters(prev => ({ ...prev, minDistance: v === DISTANCE_MIN ? '' : String(v) }))}
                unit="km"
              />
              <DualRangeSlider
                label="Duration"
                min={DURATION_MIN}
                max={DURATION_MAX}
                step={DURATION_STEP}
                lowValue={filters.minDuration ? Number(filters.minDuration) : DURATION_MIN}
                highValue={filters.maxDuration ? Number(filters.maxDuration) : DURATION_MAX}
                onLowChange={v => setFilters(prev => ({ ...prev, minDuration: v === DURATION_MIN ? '' : String(v) }))}
                onHighChange={v => setFilters(prev => ({ ...prev, maxDuration: v === DURATION_MAX ? '' : String(v) }))}
                formatLabel={(v, _bound) => {
                  if (v >= 60) {
                    const h = Math.floor(v / 60)
                    const m = v % 60
                    return m > 0 ? `${h}h${m}` : `${h}h`
                  }
                  return `${v}m`
                }}
                unit="min"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleClear}
                className="px-3 py-1.5 text-sm text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {isFiltered && !filterOpen && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {filters.pilots.map(p => (
              <span
                key={p.nick}
                className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]"
              >
                {p.name || p.nick}
                <button onClick={() => removePilotFilter(p.nick)} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            {filters.takeoffs.map(t => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]"
              >
                {t.name}
                <button onClick={() => removeTakeoffFilter(t.id)} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            {filters.after && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                From {filters.after}
                <button onClick={() => removeDateFilter('after')} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.before && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                To {filters.before}
                <button onClick={() => removeDateFilter('before')} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.flightType && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                {flightTypeLabel(filters.flightType)}
                <button onClick={() => setFilters(prev => ({ ...prev, flightType: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.hasPhotos && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                {filters.hasPhotos === 'true' ? 'Has photos' : 'No photos'}
                <button onClick={() => setFilters(prev => ({ ...prev, hasPhotos: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.hasTrack && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                {filters.hasTrack === 'true' ? 'Has track' : 'No track'}
                <button onClick={() => setFilters(prev => ({ ...prev, hasTrack: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.hasUpTrack && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                {filters.hasUpTrack === 'true' ? 'Has up track' : 'No up track'}
                <button onClick={() => setFilters(prev => ({ ...prev, hasUpTrack: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.minDistance && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                ≥ {filters.minDistance} km
                <button onClick={() => setFilters(prev => ({ ...prev, minDistance: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {(filters.minDuration || filters.maxDuration) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
                {filters.minDuration && filters.maxDuration
                  ? `${filters.minDuration}–${filters.maxDuration} min`
                  : filters.minDuration
                  ? `≥ ${filters.minDuration} min`
                  : `≤ ${filters.maxDuration} min`}
                <button onClick={() => setFilters(prev => ({ ...prev, minDuration: '', maxDuration: '' }))} className="hover:text-[#EF8354]/60">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}

        <div className="mt-8">
          <FlightList
            flights={flights}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            onAddPilotFilter={addPilotFilter}
            onAddTakeoffFilter={addTakeoffFilter}
          />
        </div>
      </div>
    </>
  )
}

function HomePage() {
  const { userData, loading } = useAuthStatus()

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-[#BFC0C0]/30 rounded w-1/4" />
          <div className="h-4 bg-[#BFC0C0]/30 rounded w-1/3" />
          <div className="mt-8 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-[#BFC0C0]/30 rounded" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return userData.authenticated ? <ActivityFeed /> : <FlightsList />
}

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { userData } = useAuthStatus()
  const [isAddFlightModalOpen, setIsAddFlightModalOpen] = useState(false)

  const navItems = userData.authenticated
    ? [
        { label: 'Feed', url: '/' },
        { label: 'Flights', url: '/flights' },
        { label: 'Adventures', url: '/adventures' },
        { label: 'Pilots', url: '/pilots' },
        { label: 'Takeoffs', url: '/takeoffs' },
        { label: 'About', url: '/about' },
      ]
    : [
        { label: 'Flights', url: '/' },
        { label: 'Adventures', url: '/adventures' },
        { label: 'Pilots', url: '/pilots' },
        { label: 'Takeoffs', url: '/takeoffs' },
        { label: 'About', url: '/about' },
      ]

  if (userData.pilot?.is_admin) {
    navItems.push({ label: 'Admin', url: '/admin' })
  }

  const handleAddFlight = async (file: File, gliderId?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (gliderId) formData.append('glider_id', gliderId)

    const response = await fetch('/v0/flights', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Failed to upload flight')
    }

    // Navigate to flights list so the new flight is visible.
    if (location.pathname === '/flights') {
      // Already on the flights page — force remount by toggling away and back.
      navigate('/', { replace: true })
      setTimeout(() => navigate('/flights', { replace: true }), 0)
    } else {
      navigate('/flights')
    }
  }

  const handleAddManualFlight = async (data: ManualFlightData) => {
    const response = await fetch('/v0/flights/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error('Failed to add manual flight')
    }

    if (location.pathname === '/flights') {
      navigate('/', { replace: true })
      setTimeout(() => navigate('/flights', { replace: true }), 0)
    } else {
      navigate('/flights')
    }
  }

  const openAddFlight = () => setIsAddFlightModalOpen(true)

  return (
    <>
      <StackedLayout
        navbar={
          <MainNavbar onAddFlight={openAddFlight} />
        }
        sidebar={
          <Sidebar>
            <SidebarHeader>
              <SidebarItem className="lg:mb-2.5" onClick={() => navigate('/')}>
                <SidebarLabel className="overflow-visible">
                  <AirtimeBrand />
                </SidebarLabel>
              </SidebarItem>

            </SidebarHeader>
            <SidebarBody>
              <SidebarSection>
                {navItems.map(({ label, url }) => (
                  <SidebarItem key={label} onClick={() => navigate(url)}>
                    {label}
                  </SidebarItem>
                ))}
              </SidebarSection>
              {userData.authenticated && (
                <SidebarSection>
                  <SidebarItem onClick={openAddFlight}>
                    <PlusIcon className="h-5 w-5" />
                    <SidebarLabel>Add Flight</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              )}
            </SidebarBody>
          </Sidebar>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/flights" element={<FlightsList />} />
          <Route path="/flight/:id" element={<FlightDetails />} />
          <Route path="/adventures" element={<AdventureList />} />
          <Route path="/adventure/:id" element={<AdventureDetails />} />
          <Route path="/takeoffs" element={<TakeoffList />} />
          <Route path="/pilots" element={<PilotList />} />
          <Route path="/me" element={<MyProfile />} />
          <Route path="/pilot/:nick" element={<PilotProfile />} />
          <Route path="/takeoff/:name" element={<TakeoffDetails />} />
          <Route path="/settings" element={<UserSettings />} />
          <Route path="/about" element={<About />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </StackedLayout>

      <AddFlightModal
        isOpen={isAddFlightModalOpen}
        onClose={() => setIsAddFlightModalOpen(false)}
        onSubmit={handleAddFlight}
        onSubmitManual={handleAddManualFlight}
      />
    </>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App;
